import { useState } from "react";
import { SKU } from "@/data/mockData";
import { useProcurement } from "@/context/ProcurementContext";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Package } from "lucide-react";
import { toast } from "sonner";

interface SKUFormProps {
  sku?: SKU;
  onSave: (skuData: Partial<SKU>) => Promise<boolean>;
  onCancel: () => void;
}

export const SKUForm = ({ sku, onSave, onCancel }: SKUFormProps) => {
  const { state } = useProcurement();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Basic Information
  const [formData, setFormData] = useState({
    skuCode: sku?.skuCode || "",
    name: sku?.name || "",
    description: sku?.description || "",
    category: sku?.category || "",
    unit: sku?.unit || "pieces",
    currentStock: sku?.currentStock || 0,
    reorderLevel: sku?.reorderLevel || 0,
    isActive: sku?.isActive ?? true,
  });


  // Validation
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Available categories and units
  const categories = Array.from(new Set(state.skus.map(s => s.category))).concat([
    'Hardware', 'Electronics', 'Tools', 'Materials', 'Components', 'Safety', 'Office Supplies'
  ]);

  const units = ['pieces', 'kg', 'g', 'meters', 'm', 'cm', 'liters', 'ml', 'sets', 'boxes', 'rolls'];

  // Validate form
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.skuCode.trim()) {
      newErrors.skuCode = "SKU Code is required";
    } else if (state.skus.some(s => s.skuCode === formData.skuCode && s.id !== sku?.id)) {
      newErrors.skuCode = "SKU Code already exists";
    }

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!formData.category.trim()) {
      newErrors.category = "Category is required";
    }

    if (formData.currentStock < 0) {
      newErrors.currentStock = "Stock cannot be negative";
    }

    if (formData.reorderLevel < 0) {
      newErrors.reorderLevel = "Reorder level cannot be negative";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fix the validation errors");
      return;
    }

    setIsSubmitting(true);

    const skuData: Partial<SKU> = {
      ...formData,
      updatedAt: new Date().toISOString(),
      ...(sku ? {} : { createdAt: new Date().toISOString(), createdBy: 1 })
    };

    const success = await onSave(skuData);
    setIsSubmitting(false);

    if (success) {
      toast.success(sku ? "SKU updated successfully" : "SKU created successfully");
    }
  };

  // Generate SKU code
  const generateSKUCode = () => {
    const categoryPrefix = formData.category ? formData.category.substring(0, 3).toUpperCase() : 'SKU';
    const timestamp = Date.now().toString().slice(-6);
    const randomSuffix = Math.random().toString(36).substring(2, 5).toUpperCase();
    const newCode = `${categoryPrefix}-${timestamp}-${randomSuffix}`;
    setFormData({ ...formData, skuCode: newCode });
  };

  return (
    <Dialog open={true} onOpenChange={onCancel}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {sku ? "Edit SKU" : "Create New SKU"}
          </DialogTitle>
          <DialogDescription>
            {sku ? "Update the SKU information and settings" : "Add a new SKU to your inventory"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">

          {/* Basic Information */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="skuCode">SKU Code *</Label>
                <div className="flex gap-2">
                  <Input
                    id="skuCode"
                    value={formData.skuCode}
                    onChange={(e) => setFormData({ ...formData, skuCode: e.target.value })}
                    placeholder="SKU-001"
                    className={errors.skuCode ? "border-red-500" : ""}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={generateSKUCode}
                    className="whitespace-nowrap"
                  >
                    Auto Generate
                  </Button>
                </div>
                {errors.skuCode && <p className="text-sm text-red-500">{errors.skuCode}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Product name"
                  className={errors.name ? "border-red-500" : ""}
                />
                {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Detailed description of the product"
                  className={errors.description ? "border-red-500" : ""}
                  rows={3}
                />
                {errors.description && <p className="text-sm text-red-500">{errors.description}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger className={errors.category ? "border-red-500" : ""}>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from(new Set(categories)).map(category => (
                      <SelectItem key={category} value={category}>{category}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.category && <p className="text-sm text-red-500">{errors.category}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="unit">Unit</Label>
                <Select value={formData.unit} onValueChange={(value) => setFormData({ ...formData, unit: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map(unit => (
                      <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="currentStock">Current Stock</Label>
                <Input
                  id="currentStock"
                  type="number"
                  value={formData.currentStock}
                  onChange={(e) => setFormData({ ...formData, currentStock: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                  className={errors.currentStock ? "border-red-500" : ""}
                />
                {errors.currentStock && <p className="text-sm text-red-500">{errors.currentStock}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="reorderLevel">Reorder Level</Label>
                <Input
                  id="reorderLevel"
                  type="number"
                  value={formData.reorderLevel}
                  onChange={(e) => setFormData({ ...formData, reorderLevel: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                  className={errors.reorderLevel ? "border-red-500" : ""}
                />
                {errors.reorderLevel && <p className="text-sm text-red-500">{errors.reorderLevel}</p>}
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
                <Label htmlFor="isActive">Active</Label>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : sku ? "Update SKU" : "Create SKU"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
